// DS: CMPST17
/** A row of TopTopicCards ending in a button for showing the AllTopicsMenu. */
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import first from 'lodash/first';
import { PapiTopic } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import BiButton from '@wix/da-bi/pkg/Components/BiButton';
import { Section, Nav } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import CloseBig from '@wix/da-ds/pkg/Icons/CloseBig';
import TopTopicCard from './TopTopicCard';
import AllTopicsMenu from './AllTopicsMenu';
import { BiData, TopicsViewallClickBiEvent } from '@wix/da-bi/pkg/events';

import s from './TopTopics.scss';

const NUM_OF_TOPICS_SHOWN = 7;
const MARGIN_BETWEEN_CARDS = parseInt(s['margin-between-cards'], 10) || 8;
const MIN_ROW_WIDTH = parseInt(s['min-row-width'], 10) || 1056;

export interface Props {
  /** Optional title above the strip of topics */
  title?: string;
  /** space available to this component, used to determine card sizes */
  availableWidth: number;
  /** an array of topics considered most popular */
  topTopics: PapiTopic[];
  /** optional array of all topics available, if given will show togglable 'all topics' menu */
  allTopics?: PapiTopic[];
  className?: string;
}

const TopTopics: React.FC<Props> = props => {
  const { title, topTopics, allTopics, className } = props;
  const availableWidth = Math.max(props.availableWidth, MIN_ROW_WIDTH);

  const isShowingAllTopicsButton = !!allTopics;
  const numOfColumns = isShowingAllTopicsButton
    ? NUM_OF_TOPICS_SHOWN + 1
    : NUM_OF_TOPICS_SHOWN;
  const pxBetweenCards = (numOfColumns - 1) * MARGIN_BETWEEN_CARDS;
  const cardSpace = availableWidth - pxBetweenCards;
  const cardWidth = Math.floor(cardSpace / numOfColumns);
  const remainingWidth = cardSpace - cardWidth * numOfColumns;

  // the last card is a button that can open a larger menu that shows
  // text links to all the topics
  const [isAllTopicsMenuOpen, setAllTopicsMenuOpen] = useState(false);
  const toggleAllTopicsMenu = useCallback(
    () => setAllTopicsMenuOpen(!isAllTopicsMenuOpen),
    [isAllTopicsMenuOpen, setAllTopicsMenuOpen]
  );

  if (!topTopics || !topTopics.length) {
    return null;
  }

  return (
    <Section className={classnames(s['root'], className)}>
      <BiLoggerContextProvider value={{ sectionname: 'topTopicCard' }}>
        {title && <Heading className={s['title']}>{title}</Heading>}

        <Nav className={s['row']}>
          {topTopics.map((topic: PapiTopic, index) => (
            <TopTopicCard
              key={topic.name}
              typeId={topic.typeId}
              itemId={topic.itemId}
              name={topic.name}
              url={topic.url!}
              deviation={first(topic.exampleDeviations)}
              width={cardWidth}
              className={s['top-topic-card']}
              rank={index}
            />
          ))}
          {isShowingAllTopicsButton && (
            <>
              <ViewAllTopicsButton
                isOpened={isAllTopicsMenuOpen}
                onClick={toggleAllTopicsMenu}
                width={cardWidth + remainingWidth}
              />
              {isAllTopicsMenuOpen && allTopics && (
                <AllTopicsMenu
                  allTopics={allTopics}
                  className={s['all-topics-menu']}
                />
              )}
            </>
          )}
        </Nav>
      </BiLoggerContextProvider>
    </Section>
  );
};
TopTopics.displayName = 'TopTopicsCMPST17';
export default TopTopics;

/** A button that opens the 'All Topics' menu */
export function ViewAllTopicsButton({
  isOpened,
  onClick,
  width,
}: {
  isOpened?: boolean;
  onClick?: (e) => void;
  width?: number;
}) {
  const { t } = useTranslation();
  const className = classnames(s['all-topics-button'], isOpened && s['opened']);
  return (
    <BiButton
      onClick={onClick}
      style={{ width }}
      className={className}
      biData={BiData<TopicsViewallClickBiEvent>({
        evid: 114,
        is_expanding: isOpened ? 0 : 1,
      })}
      aria-haspopup="true"
      aria-controls="AllTopicsMenu"
      aria-expanded={isOpened}
    >
      {isOpened ? (
        <CloseBig className={s['close-icon']} />
      ) : (
        t('topicListPage.viewAll')
      )}
    </BiButton>
  );
}
